import { Ionicons } from '@expo/vector-icons';
import { setAudioModeAsync, useAudioPlayer, useAudioPlayerStatus } from 'expo-audio';
import React, { useEffect, useRef } from 'react';
import { LOADING_SPINNER_COLOR } from '@/lib/loadingSpinnerColor';
import { ActivityIndicator, Platform, TouchableOpacity, View } from 'react-native';
import { SizableText, XStack, YStack } from 'tamagui';
import { CardTitle } from '@/components/ui/Typography';

interface AudioMaterialItemProps {
  title: string;
  url: string;
  onDownload?: () => void;
  isDownloading?: boolean;
  icon?: React.ReactNode;
}

function formatTime(seconds: number): string {
  if (!seconds || !isFinite(seconds)) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

// Native audio player component using expo-audio directly
const NativeAudioPlayer = ({ 
  title, 
  url, 
  onDownload, 
  isDownloading, 
  icon 
}: AudioMaterialItemProps) => {
  const player = useAudioPlayer(url);
  const status = useAudioPlayerStatus(player);
  const barWidthRef = useRef(0);

  // expo-audio never configures the AVAudioSession on its own, so the app runs
  // in iOS's default `soloAmbient` category and the ring/silent switch mutes
  // play-alongs (the progress bar moves, nothing is heard). Opt into playback
  // in silent mode, like every music app — and like Safari, which ignores the
  // switch for <audio>, hence "works in the browser" reports.
  //
  // This must happen right before EACH play, not once on mount: the lesson
  // video is a WKWebView, and WebKit resets the shared AVAudioSession category
  // to ambient whenever its own media player initialises or pauses, which
  // lands after this component mounts and silently undoes an earlier call.
  const ensurePlaybackAudioMode = async () => {
    try {
      await setAudioModeAsync({ playsInSilentMode: true });
    } catch {
      // Non-fatal: playback still works, just subject to the silent switch.
    }
  };

  // Stop audio on unmount
  useEffect(() => {
    return () => {
      try {
        player?.pause();
      } catch (e) {
        // Player may already be released
      }
    };
  }, [player]);

  const progress = status.duration > 0 ? status.currentTime / status.duration : 0;

  const handlePlayPause = async () => {
    if (status.playing) {
      player.pause();
    } else {
      await ensurePlaybackAudioMode();
      player.play();
    }
  };

  const handleSeek = (evt: any) => {
    if (status.duration <= 0 || barWidthRef.current <= 0) return;
    const x = evt.nativeEvent?.locationX;
    if (typeof x === 'number') {
      const ratio = Math.max(0, Math.min(1, x / barWidthRef.current));
      player.seekTo(ratio * status.duration);
    }
  };

  return (
    <YStack 
      gap="$3" 
      p="$3" 
      br="$4" 
      backgroundColor="#F8F7F4"
    >
      {/* Title row */}
      <XStack ai="center" gap="$3">
        {icon}
        <CardTitle color="#0D0D0D" flex={1} numberOfLines={2}>
          {title}
        </CardTitle>
        {onDownload && (
          <TouchableOpacity
            onPress={onDownload}
            disabled={isDownloading}
            style={{
              padding: 8,
              borderRadius: 8,
              opacity: isDownloading ? 0.6 : 1,
            }}
          >
            {isDownloading ? (
              <ActivityIndicator size="small" color={LOADING_SPINNER_COLOR} />
            ) : (
              <Ionicons name="download-outline" size={22} color="#666" />
            )}
          </TouchableOpacity>
        )}
      </XStack>
      
      {/* Player controls row */}
      <XStack ai="center" gap="$3">
        {/* Play/Pause button */}
        <TouchableOpacity
          onPress={handlePlayPause}
          style={{
            width: 32,
            height: 32,
            borderRadius: 16,
            backgroundColor: '#0D0D0D',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Ionicons 
            name={status.playing ? 'pause' : 'play'} 
            size={16} 
            color="#FFFFFF" 
            style={status.playing ? {} : { marginLeft: 2 }}
          />
        </TouchableOpacity>

        {/* Time and progress */}
        <XStack flex={1} ai="center" gap="$2">
          <SizableText size="$2" color="#666" fontFamily="$mono" w={42}>
            {formatTime(status.currentTime)}
          </SizableText>
          
          <TouchableOpacity
            style={{ flex: 1, height: 32, justifyContent: 'center' }}
            onPress={handleSeek}
            onLayout={(e) => { barWidthRef.current = e.nativeEvent.layout.width; }}
            activeOpacity={1}
          >
            <View style={{ 
              height: 6, 
              backgroundColor: '#E0DED8', 
              borderRadius: 3, 
              overflow: 'hidden' 
            }}>
              <View style={{ 
                width: `${progress * 100}%`, 
                height: '100%', 
                backgroundColor: '#0D0D0D', 
                borderRadius: 3 
              }} />
            </View>
          </TouchableOpacity>
          
          <SizableText size="$2" color="#666" fontFamily="$mono" w={42} ta="right">
            {formatTime(status.duration)}
          </SizableText>
        </XStack>
      </XStack>
    </YStack>
  );
};

export const AudioMaterialItem = React.memo(({
  title,
  url,
  onDownload,
  isDownloading = false,
  icon,
}: AudioMaterialItemProps) => {
  // Web: Clean card with native audio controls
  if (Platform.OS === 'web') {
    return (
      <YStack 
        gap="$3" 
        p="$3" 
        br="$4" 
        style={{ backgroundColor: '#F8F7F4' }}
      >
        <XStack ai="center" gap="$3">
          {icon}
          <CardTitle color="#0D0D0D" flex={1}>
            {title}
          </CardTitle>
        </XStack>
        <audio
          controls
          src={url}
          preload="metadata"
          style={{ 
            width: '100%', 
            height: 44,
            borderRadius: 8,
          }}
        />
      </YStack>
    );
  }

  // Native: Custom UI with expo-audio
  return (
    <NativeAudioPlayer
      title={title}
      url={url}
      onDownload={onDownload}
      isDownloading={isDownloading}
      icon={icon}
    />
  );
});

AudioMaterialItem.displayName = 'AudioMaterialItem';
